import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import Loading from "@/components-legacy/loading";
import { Badge, BadgeProps } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import CopyAndIcon from "@/pages/legacy/settings/settingsComponents/copyAndIcon";
import useRemoveDomain from "@babackapp/private-api-client/client/use-delete-domain";
import useTestDomainStatus from "@babackapp/private-api-client/client/use-test-domain-status";
import useTranslation from "@babackapp/translations/use-translation";
import { ShopDomainDto } from "@babackapp/private-api-client/types";
import useDomainDnsRecordsDownloader from "./use-download-csv-records";

type DomainListItemProps = {
  domain: ShopDomainDto;
};

export function DomainListItem({ domain }: DomainListItemProps) {
  const t = useTranslation();

  const { data: shop } = useCurrentShopSuspense();
  const downloadDnsRecords = useDomainDnsRecordsDownloader()

  const { mutateAsync: removeDomain, isPending: removeDomainPending } =
    useRemoveDomain(shop);

  const { mutateAsync: testDomain, isPending: testDomainPending } =
    useTestDomainStatus(shop);

  const isPending = removeDomainPending || testDomainPending;

  return (
    <div className="flex px-4 border border-border items-center group hover:bg-muted rounded-lg p-2">
      <div className="flex flex-col flex-1">
        <div className="flex gap-2 items-center">
          <span className="text-sm font-medium py-2">{domain.name}</span>
          <Badge
            variant={
              ({
                pending: "warningoutline",
                active: "ouline",
                error: "destructive",
              }[domain.status] ?? "outline") as BadgeProps["variant"]
            }
          >
            {domain.status}
          </Badge>
        </div>
      </div>
      {isPending ? (
        <Loading className="w-4 h-4" />
      ) : (
        <ActionBar
          domain={domain}
          onDownloadDnsRecords={() => downloadDnsRecords(domain)}
          onTestDomain={() => testDomain()}
          onRemove={() => removeDomain(domain.name)}
        />
      )}
    </div>
  );
}

type ActionBarProps = {
  domain: ShopDomainDto;
  onRemove: () => void;
  onTestDomain: () => void;
  onDownloadDnsRecords: () => void;
};

function ActionBar({ domain, onRemove, onTestDomain, onDownloadDnsRecords }: ActionBarProps) {
  const t = useTranslation();

  return (
    <div className="flex items-center transition-all group-hover:opacity-100 opacity-0">
      {domain.status === "pending" && (
        <>
          <Button
            variant="link"
            size="sm"
            className="text-muted-foreground"
            onClick={onDownloadDnsRecords}
          >
            {t("settings.domains.downloadDnsRecord")}
          </Button>
          <Button
            onClick={onTestDomain}
            variant="link"
            size="sm"
            className="text-muted-foreground"
          >
            {t("settings.domains.checkUp")}
          </Button>
        </>
      )}
      <Button
        onClick={onRemove}
        variant="link"
        size="sm"
        className="text-muted-foreground"
      >
        {t("misc.remove")}
      </Button>
    </div>
  );
}
